/* eslint-disable */
"use client";
import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import svgPaths from "@/svg/headerPaths";
import {
  img4,
  img5,
} from "@/imports/svg-59rgz";
import { PersonalizationToast } from "@/components/common/PersonalizationToast";
import BottomToast from "@/components/common/BottomToast";
import { getAuthStatus } from "@/lib/auth";
import ProfileModal from "@/components/common/ProfileModal";
import { authService } from "@/services/auth.service";
import { 
  getPersonalizationEnabled, 
  setPersonalizationEnabled, 
  checkPersonalizationAvailable,
  getPersonalizationEnabledFor,
  setPersonalizationEnabledFor
} from "@/lib/personalization";

function SearchSuggestionsList({ prompts, onPick }: { prompts: string[]; onPick: (text: string) => void }) {
  const safe = Array.isArray(prompts) ? prompts.filter(Boolean) : [];
  if (safe.length === 0) return null;
  return (
    <div className="content-stretch flex flex-col gap-1 lg:gap-2 items-start justify-start relative shrink-0 w-full">
      {safe.map((p, idx) => (
        <button
          key={idx}
          onMouseDown={(e) => { e.preventDefault(); onPick(p); }}
          className="flex items-center text-left hover:opacity-80 transition-opacity cursor-pointer py-0.5 lg:py-1"
        >
          <span className="text-white/90 text-sm lg:text-base" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>{p}</span>
        </button>
      ))}
    </div>
  );
}

function RecommendedSearchDropdown({ isOpen, prompts, onPick, isLoggedIn }: { isOpen: boolean; prompts: string[]; onPick: (text: string) => void; isLoggedIn: boolean }) {
  if (!isOpen) return null;
  return (
    <div className="absolute bg-[#010103] box-border content-stretch flex flex-col gap-4 lg:gap-6 items-start justify-start left-0 px-4 lg:px-6 py-6 lg:py-8 rounded-bl-[20px] lg:rounded-bl-[26px] rounded-br-[20px] lg:rounded-br-[26px] shadow-[0px_0px_18px_0px_rgba(162,171,173,0.2)] top-[20px] lg:top-[22px] w-full max-w-[calc(100vw-24px)] lg:max-w-[800px] z-50">
      <div className="w-full h-px opacity-40">
        <img className="w-full h-full" src={img5} alt="분리선" />
      </div>
        <div className="content-stretch flex flex-col gap-2 items-start justify-start relative shrink-0 w-full">
          {isLoggedIn ? (
            <>
              <div className="text-white/90 text-sm lg:text-lg font-medium mb-1" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>
                추천 검색어
              </div>
              <SearchSuggestionsList prompts={prompts} onPick={onPick} />
            </>
          ) : (
            <div className="text-white/70 text-sm lg:text-base" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>
              로그인을 하시면 검색어 추천을 해드립니다.
            </div>
          )}
        </div>
    </div>
  );
}

function PersonalizeIcon({ isActive }: { isActive: boolean }) {
  return (
    <div className="w-[20px] h-[20px] lg:w-[26px] lg:h-[26px]">
      {isActive ? (
        <img className="w-full h-full" src={img4} alt="개인화 활성" />
      ) : (
        <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 26 26">
          <g>
            <g opacity="0.6">
              <path d={svgPaths.p16e23500} fill="white" />
              <path d={svgPaths.p9556200} fill="white" />
              <path d={svgPaths.p3e5a2f70} fill="white" />
              <path d={svgPaths.p2130f070} fill="white" />
              <path d={svgPaths.p1e16afc0} fill="white" />
              <path d={svgPaths.p100aa300} fill="white" />
            </g>
          </g>
        </svg>
      )}
    </div>
  );
}

function PersonalizeButton({ isActive, onClick }: { isActive: boolean; onClick: () => void }) {
  return (
    <button className="content-stretch flex flex-col h-12 lg:h-14 items-center justify-center relative shrink-0 w-[50px] lg:w-[75px] cursor-pointer hover:opacity-80 transition-opacity" onClick={onClick}>
      <PersonalizeIcon isActive={isActive} />
    </button>
  );
}

function SearchIcon() {
  return (
    <div className="h-5 w-5 lg:h-7 lg:w-7 relative shrink-0">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 28 28">
        <g>
          <circle cx="12.25" cy="12.25" r="7.583" stroke="var(--stroke-0, white)" strokeMiterlimit="10" strokeWidth="1.76" />
          <path d="M17.5 17.5L23.333 23.333" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeWidth="1.76" />
        </g>
      </svg>
    </div>
  );
}

function SearchIconArea({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} aria-label="검색"
      className="content-stretch flex flex-col h-12 lg:h-14 items-center justify-center overflow-clip relative shrink-0 w-[50px] lg:w-[60px] cursor-pointer">
      <SearchIcon />
    </button>
  );
}

function SearchInputForm({ onFocus, onBlur, value, onChange, onSubmit, placeholder }: { onFocus: () => void; onBlur: () => void; value: string; onChange: (value: string) => void; onSubmit: () => void; placeholder?: string }) {
  return (
    <div className="basis-0 grow h-12 lg:h-14 min-h-px min-w-px relative shrink-0">
      <div className="flex flex-row items-center relative size-full">
        <div className="box-border content-stretch flex h-12 lg:h-14 items-center justify-start pl-0 pr-[30px] lg:pr-[50px] py-2 lg:py-4 relative w-full">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit(); } }}
            placeholder={placeholder || "요즘 비즈니스 트렌드 궁금하세요? 말하듯 편하게 검색하세요!"}
            className="basis-0 bg-transparent border-none outline-none font-['Pretendard:Medium',_sans-serif] grow h-5 lg:h-6 min-h-px min-w-px not-italic relative shrink-0 text-sm lg:text-[16px] text-[rgba(255,255,255,0.8)] tracking-[-0.32px] placeholder:text-[rgba(255,255,255,0.8)] placeholder:leading-[18px] lg:placeholder:leading-[21px]"
            style={{ fontSize: "14px", fontWeight: 500, letterSpacing: "-0.28px", lineHeight: "18px" }}
          />
        </div>
      </div>
    </div>
  );
}

function SearchInputBar({ personalizeActive, onPersonalizeToggle, onSearchFocus, onSearchBlur, searchValue, onSearchChange, onSubmit, placeholder }: {
  personalizeActive: boolean;
  onPersonalizeToggle: () => void;
  onSearchFocus: () => void;
  onSearchBlur: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
}) {
  return (
    <div className="absolute bg-[#010103] content-center flex flex-wrap gap-px h-12 lg:h-14 items-center justify-start left-0 rounded-[24px] lg:rounded-[28px] top-0 w-full z-[60]">
      <PersonalizeButton isActive={personalizeActive} onClick={onPersonalizeToggle} />
      <div className="h-[16px] lg:h-[22px] relative shrink-0 w-px">
        <div className="absolute bottom-0 left-[-50%] right-1/2 top-0">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 2 22">
            <path d="M1 0V22" stroke="white" strokeOpacity="0.4" />
          </svg>
        </div>
      </div>
      <SearchIconArea onClick={onSubmit} />
      <SearchInputForm onFocus={onSearchFocus} onBlur={onSearchBlur} value={searchValue} onChange={onSearchChange} onSubmit={onSubmit} placeholder={placeholder} />
    </div>
  );
}

function SearchInputFormGroup({ searchFocused, personalizeActive, onPersonalizeToggle, onSearchFocus, onSearchBlur, searchValue, onSearchChange, prompts, onPickPrompt, onSubmit, isLoggedIn, placeholder }: {
  searchFocused: boolean;
  personalizeActive: boolean;
  onPersonalizeToggle: () => void;
  onSearchFocus: () => void;
  onSearchBlur: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  prompts: string[];
  onPickPrompt: (text: string) => void;
  onSubmit: () => void;
  isLoggedIn: boolean;
  placeholder?: string;
}) {
  return (
    <div className="relative size-full overflow-visible">
      <RecommendedSearchDropdown isOpen={searchFocused} prompts={prompts} onPick={onPickPrompt} isLoggedIn={isLoggedIn} />
      <SearchInputBar
        personalizeActive={personalizeActive}
        onPersonalizeToggle={onPersonalizeToggle}
        onSearchFocus={onSearchFocus}
        onSearchBlur={onSearchBlur}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onSubmit={onSubmit}
        placeholder={placeholder}
      />
    </div>
  );
}

interface SearchComponentProps {
  className?: string;
  placeholder?: string;
}

export function SearchComponent({ className = "", placeholder = "요즘 비즈니스 트렌드 궁금하세요? 말하듯 편하게 검색하세요!" }: SearchComponentProps) {
  const [searchValue, setSearchValue] = useState("");
  const [personalizeActive, setPersonalizeActive] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | undefined>(undefined);
  const [toastCtaText, setToastCtaText] = useState<string | undefined>(undefined);
  const [toastHideCTA, setToastHideCTA] = useState<boolean>(false);
  const [loginToastVisible, setLoginToastVisible] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [hasPersonalization, setHasPersonalization] = useState<boolean | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState<string | null>(null);
  const [userCompany, setUserCompany] = useState<string | null>(null);
  const [promptList, setPromptList] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();

  // 모바일 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg 브레이크포인트 기준
    };
    
    checkMobile(); // 초기 체크
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // URL에서 검색어 초기화
  useEffect(() => {
    try {
      const qs = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      const qv = (qs.get('q') || '').trim();
      setSearchValue(qv);
    } catch {}
  }, []);

  // 외부에서 검색어 초기화를 요청하는 커스텀 이벤트 처리
  useEffect(() => {
    function onClear() { setSearchValue(''); }
    try { window.addEventListener('dw:clear_search', onClear as any); } catch {}
    return () => { try { window.removeEventListener('dw:clear_search', onClear as any); } catch {} };
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleSearchSubmit = () => {
    if (searchValue.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  const handlePersonalizeToggle = async () => {
    if (!userName) {
      setLoginToastVisible(true);
      return;
    }
    try {
      // 개인화 저장 여부 확인 (캐시 없거나 미저장 상태에서는 서버 확인)
      let exists = hasPersonalization;
      if (exists === null || exists === false) {
        exists = await checkPersonalizationAvailable();
        setHasPersonalization(!!exists);
      }
      
      // 현재 개인화 상태를 토글
      const newPersonalizeState = !personalizeActive;
      
      if (newPersonalizeState) {
        // 개인화 켜기
        setPersonalizeActive(true);
        if (userId) setPersonalizationEnabledFor(userId, true);
        else setPersonalizationEnabled(true);
        
        // 개인화 설정이 켜졌을 때 토스트 표시
        setToastMessage('개인화 설정이 켜졌습니다.');
        setToastCtaText('설정하러가기');
        setToastHideCTA(false);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      } else {
        // 개인화 끄기
        setPersonalizeActive(false);
        if (userId) setPersonalizationEnabledFor(userId, false);
        else setPersonalizationEnabled(false);
        
        // 개인화 설정이 꺼졌을 때 토스트 표시
        setToastMessage('개인화 설정이 해제되었습니다.');
        setToastCtaText('설정하러가기');
        setToastHideCTA(false);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      }
      
      // 개인화 데이터가 없는 경우 안내
      if (!exists && newPersonalizeState) {
        setPersonalizeActive(false);
        if (userId) setPersonalizationEnabledFor(userId, false);
        else setPersonalizationEnabled(false);
        setToastMessage('개인화 서비스를 이용하시려면 관심사 설정이 필요합니다.');
        setToastCtaText('관심사 설정');
        setToastHideCTA(false);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      }
    } catch {
      // 오류 시에도 사용자 경험상 안내 토스트 유지, 아이콘은 OFF
      setPersonalizeActive(false);
      if (userId) setPersonalizationEnabledFor(userId, false); else setPersonalizationEnabled(false);
      setToastMessage('개인화 서비스를 이용하시려면 관심사 설정이 필요합니다.');
      setToastCtaText('관심사 설정');
      setToastHideCTA(false);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
    }
  };

  // 개인화 활성 상태가 바뀌면 전역값/이벤트는 커밋 이후(Effect)로 전달하여 교차 렌더 업데이트 에러 방지
  useEffect(() => {
    try { (window as any).__dw_personalize_active__ = personalizeActive; } catch {}
    try { window.dispatchEvent(new CustomEvent('dw:personalize_toggle', { detail: personalizeActive })); } catch {}
  }, [personalizeActive]);

  const handleAgreeClick = () => {
    try {
      // 마이페이지 모달을 개인화 설정 탭으로 오픈
      setProfileOpen(true);
      // 약간의 지연 후 탭 전환 플래그를 window에 기록 (모달 마운트 이후 적용 보조)
      (window as any).__dw_profile_initial_menu__ = 'personalization';
    } catch {}
  };

  useEffect(() => {
    getAuthStatus()
      .then(async (data) => {
        // To-Be 형식 처리
        if (data?.ok && data?.user) {
          const user = data?.user;
          if (user?.name) {
            setUserName(user.name as string);
            try { setUserEmail(user.email as string); } catch {}
            try { setUserId(String(user.id ?? '')); } catch {}
            try {
              const pj = await authService.getPersonalization();
              // To-Be 형식 처리
              const d = pj?.code === "SUCCESS" ? (pj.data ?? {}) : {};
              const exists = !!(String(d?.job || '').trim()) || !!(String(d?.interests || '').trim());
              setHasPersonalization(exists);
              const prompts = Array.isArray(d?.prompts) ? d.prompts.filter((s: any) => typeof s === 'string' && s.trim()) : [];
              setPromptList(prompts);
                
              // 로그인 시 사용자별 로컬스토리지에서 개인화 설정 불러오기
              if (exists) {
                const pref = user?.id ? getPersonalizationEnabledFor(String(user.id)) : getPersonalizationEnabled();
                setPersonalizeActive(pref);
                (window as any).__dw_personalize_active__ = pref;
                window.dispatchEvent(new CustomEvent('dw:personalize_toggle', { detail: pref }));
              } else {
                // 개인화 데이터가 없으면 전역 비활성화 (사용자별 저장값은 보존)
                setPersonalizeActive(false);
                (window as any).__dw_personalize_active__ = false;
                window.dispatchEvent(new CustomEvent('dw:personalize_toggle', { detail: false }));
              }
              
              // 로그인 이벤트 발생
              window.dispatchEvent(new CustomEvent('dw:auth_change', { detail: { isLoggedIn: true } }));
              // 로그인 직후 백엔드가 prompts를 생성/저장하는 동안 약간의 지연 후 재조회하여 드롭다운 즉시 반영
              setTimeout(async () => {
                try {
                  const j2 = await authService.getPersonalization();
                  // To-Be 형식 처리
                  const d2 = j2?.code === "SUCCESS" ? (j2.data ?? {}) : {};
                  const prompts2 = Array.isArray(d2?.prompts) ? d2.prompts.filter((s: any) => typeof s === 'string' && s.trim()) : [];
                  if (prompts2.length) setPromptList(prompts2);
                } catch {}
              }, 1500);
              
            } catch {}
          }
        } else {
          // 로그아웃 상태: 사용자별 저장값은 보존하되 전역 활성만 false로
          setPersonalizeActive(false);
          (window as any).__dw_personalize_active__ = false;
          window.dispatchEvent(new CustomEvent('dw:personalize_toggle', { detail: false }));
          // 로그아웃 이벤트 발생
          window.dispatchEvent(new CustomEvent('dw:auth_change', { detail: { isLoggedIn: false } }));
        }
      })
      .catch(() => {})
      .finally(() => { setAuthLoading(false); });
  }, []);

  // 개인화 저장 성공 이벤트 수신 → 즉시 재조회
  useEffect(() => {
    async function onSaved() {
      try {
        const pj = await authService.getPersonalization();
        // To-Be 형식 처리
        const d = pj?.code === "SUCCESS" ? (pj.data ?? {}) : {};
        const prompts = Array.isArray(d?.prompts) ? d.prompts.filter((s: any) => typeof s === 'string' && s.trim()) : [];
        setPromptList(prompts);
      } catch {}
    }
    try { window.addEventListener('dw:personalization_saved', onSaved as any); } catch {}
    return () => { try { window.removeEventListener('dw:personalization_saved', onSaved as any); } catch {} };
  }, []);

  const handleSearchFocus = () => {
    setSearchFocused(true);
  };

  const handleSearchBlur = () => {
    setSearchFocused(false);
  };

  const handlePickPrompt = (text: string) => {
    setSearchValue(text);
    setSearchFocused(false);
    handleSearchSubmit();
  };

  // 모바일과 데스크탑에서 다른 placeholder 사용
  const dynamicPlaceholder = isMobile 
    ? "말하듯 편안하게 검색하세요!" 
    : placeholder;

  return (
    <>
      <div className={`w-full max-w-[calc(100vw-24px)] lg:max-w-[800px] h-12 lg:h-14 relative overflow-visible mx-auto ${className}`}>
        <SearchInputFormGroup
          searchFocused={searchFocused}
          personalizeActive={personalizeActive}
          onPersonalizeToggle={handlePersonalizeToggle}
          onSearchFocus={handleSearchFocus}
          onSearchBlur={handleSearchBlur}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          prompts={promptList}
          onPickPrompt={handlePickPrompt}
          onSubmit={handleSearchSubmit}
          isLoggedIn={!!userName}
          placeholder={dynamicPlaceholder}
        />
      </div>
      
      {/* 토스트 컴포넌트들 */}
      {showToast && (
        <PersonalizationToast
          isVisible={showToast}
          message={toastMessage}
          ctaText={toastCtaText}
          hideCTA={toastHideCTA}
          onAgree={handleAgreeClick}
          onClose={() => setShowToast(false)}
        />
      )}
      
      <BottomToast
        isVisible={loginToastVisible}
        message="로그인이 필요한 서비스입니다."
        variant="error"
        onClose={() => setLoginToastVisible(false)}
      />
      
      {profileOpen && (
        <ProfileModal
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          onLogout={() => {
            // 로그아웃 로직
            setProfileOpen(false);
          }}
          userId={userId || undefined}
          defaultName={userName}
          defaultEmail={userEmail}
          defaultPhone={userPhone}
          defaultCompany={userCompany}
          initialMenu="personalization"
        />
      )}
    </>
  );
}
